// Fabric.js-backed annotation overlay for OpenSeadragon.
// Drop-in replacement for the previous Annotorious wrapper. Mirrors its
// public API so show_exhibit.js stays mostly untouched:
//   anno.setDrawingTool(name) | anno.loadAnnotations(url) | anno.cancelSelected()
//   anno.destroy() | anno.readOnly = bool | anno.on(event, fn)
//   anno.setSelectionOnly(bool) | anno.clearAiRegions() | anno.persistAiRegion()
//   anno.showAiRegion(region)                                                     (AI "explain this region" mode)
// Events: 'createAnnotation' | 'updateAnnotation' | 'deleteAnnotation' | 'aiRegionSelected'.

import { Canvas, Rect, Ellipse, Line, Polygon, Textbox, Circle, util } from 'fabric';

const TEXT_BASE_FONT_PX = 24;
const STROKE_BASE_PX = 2; // intended on-screen stroke thickness at home zoom (matches old low-res look)
const POLYGON_CLOSE_THRESHOLD_PX = 12;
const MIN_SHAPE_SCREEN_PX = 10; // smaller drags are discarded as accidental clicks
const COUNTER_SCALE_EXPONENT = 0.5; // partial: scale = (s/s0)^(exponent - 1)
const COLOR_STROKE = '#FF5252';
const COLOR_AI_REGION = '#2D9CDB'; // non-persisted "explain this region" selection
const COLOR_FILL_SHAPE = 'transparent';
const COLOR_FILL_TEXT = '#ffffff';
const COLOR_TEXT_BG = 'rgba(0,0,0,0.55)';

// ── OCR word/region highlight overlay ───────────────────────────────────────
// zoom ratio (current image scale / home image scale) past which individual
// word boxes render; below this they'd be too dense to be useful
const WORD_ZOOM_THRESHOLD = 1.15;
// on-screen stroke thickness for AI-detected text/object boxes at home zoom.
// Kept as its own constant (rather than derived once at load time) because it's
// re-applied on every viewport update — see _refreshOcrStrokeWidths().
const WORD_STROKE_BASE_PX = STROKE_BASE_PX;
const ROLE_LABELS = {
    main_content: 'Body Text',
    header: 'Header',
    footer: 'Footer',
    page_number: 'Page Number',
    title_caption: 'Title / Caption',
    caption_or_reference: 'Caption / Reference',
    watermark_credit: 'Watermark / Credit',
    marginalia: 'Marginalia',
    embedded_figure: 'Embedded Figure',
    legend: 'Legend / Key',
};
const ROLE_COLORS = {
    main_content: '#90A4AE',
    header: '#42A5F5',
    footer: '#42A5F5',
    page_number: '#26C6DA',
    title_caption: '#AB47BC',
    caption_or_reference: '#AB47BC',
    watermark_credit: '#EF5350',
    marginalia: '#8D6E63',
    embedded_figure: '#66BB6A',
    legend: '#FF7043',
};
const DEFAULT_ROLE_COLOR = '#FFC107';

function hexToRgba(hex, alpha) {
    const h = hex.replace('#', '');
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
}

// Ray-casting test, for clicks on a mapKurator word's curved outline.
function pointInPolygon(x, y, points) {
    let inside = false;
    for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
        const a = points[i];
        const b = points[j];
        if ((a.y > y) !== (b.y > y) && x < (b.x - a.x) * (y - a.y) / (b.y - a.y) + a.x) {
            inside = !inside;
        }
    }
    return inside;
}

function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c]));
}

const COPY_ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>';
const CHECK_ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';

// navigator.clipboard only exists on secure origins; fall back to execCommand
// so copying still works when the app is served over plain http
function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
        return navigator.clipboard.writeText(text);
    }
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try {
        document.execCommand('copy');
    } finally {
        document.body.removeChild(ta);
    }
    return Promise.resolve();
}

// shared selection visuals — contrasting cyan to differentiate from red shape strokes
const SELECTION_STYLE = {
    borderColor: '#00E5FF',
    borderDashArray: [6, 4],
    cornerColor: '#ffffff',
    cornerStrokeColor: '#00E5FF',
    cornerSize: 12,
    cornerStyle: 'circle',
    transparentCorners: false,
    borderScaleFactor: 2,
    padding: 6,
};

function uuid() {
    return 'a-' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
}

const PERSIST_KEYS = ['id', 'counterScale', 'shapeType', 'intendedScaleX', 'intendedScaleY'];

export class FabricAnnotator {
    constructor(viewer, opts = {}) {
        this.viewer = viewer;
        this.imageId = opts.imageId ?? null;
        this.csrfToken = opts.csrfToken ?? '';
        this._readOnly = opts.readOnly !== false;
        this.tool = 'rect';
        this._handlers = { createAnnotation: [], updateAnnotation: [], deleteAnnotation: [], aiRegionSelected: [] };
        // AI selection mode: capture every pointer (no shift needed), never touch persisted shapes
        this._selectionOnly = false;
        this._activeDrawing = null;
        this._polyState = null; // { points: [...], previewLine }
        this._suppressEvents = false;
        this._activePopover = null;
        // increments per loadAnnotations() call so a slow response can't overwrite
        // the result of a newer one
        this._loadToken = 0;
        // url whose annotations are on the canvas, and the one currently being
        // fetched — used to no-op redundant reloads of the same image
        this._loadedUrl = null;
        this._loadedImageId = null;
        this._pendingUrl = null;
        this._awaitingPosition = false;

        this._shiftHeld = false;

        // expose fabric primitives for external callers (line-path animations etc.)
        this.Line = Line;
        this.util = util;

        this._initOverlay();
        this._bindOSD();
        this._bindFabricEvents();
        this._bindShiftTracking();
        this._setReadOnlyDom(this._readOnly);
        this._updateOverlaySize();
    }

    // ── public API ──────────────────────────────────────────────────────────

    on(event, fn) {
        if (this._handlers[event]) this._handlers[event].push(fn);
    }

    setDrawingTool(name) {
        this._cancelInProgressDraw();
        this.canvas.discardActiveObject();
        this.canvas.requestRenderAll();
        this.tool = name;
    }

    setImageId(id) {
        this.imageId = id;
    }

    // "Explain this region" mode: the visitor drags one rectangle that is sent to
    // the AI instead of being saved. Persisted admin shapes stay inert meanwhile.
    setSelectionOnly(on) {
        this._selectionOnly = !!on;
        if (this.canvas) {
            this.canvas.defaultCursor = this._selectionOnly ? 'crosshair' : 'default';
            this.canvas.hoverCursor = this._selectionOnly ? 'crosshair' : 'move';
        }
        if (!this._selectionOnly) this._cancelInProgressDraw();
        this._setReadOnlyDom(this._readOnly);
    }

    // Draw the rectangle of a stored explanation (image fractions) without a drag,
    // so re-opening a thread outlines the area the AI actually looked at.
    // Returns the same screen anchor _finishAiRegion hands the page, or null.
    showAiRegion(region) {
        const tiledImage = this.viewer.world.getItemAt(0);
        const size = tiledImage ? tiledImage.getContentSize() : null;
        if (!size || !size.x || !size.y) return null;

        this.clearAiRegions();

        const left = region.left * size.x;
        const top = region.top * size.y;
        const width = region.width * size.x;
        const height = region.height * size.y;

        const r = new Rect({
            left, top, width, height,
            fill: hexToRgba(COLOR_AI_REGION, 0.12),
            stroke: COLOR_AI_REGION,
            strokeWidth: STROKE_BASE_PX / (this._homeImageScale() || 1),
            strokeDashArray: [8, 5],
            strokeUniform: true,
            objectCaching: false,
            selectable: false,
            evented: false,
            hasControls: false,
            hasBorders: false,
            ...SELECTION_STYLE,
        });
        // persistAiRegion() looks this up by kind - without it the rectangle
        // silently stops being saved alongside the dot
        r.data = { kind: 'ai-region' };
        this.canvas.add(r);
        this.canvas.requestRenderAll();

        return this._screenBBox(left, top, width, height);
    }

    // screen bbox of an image-px rect, correct when the viewer is rotated
    _screenBBox(left, top, width, height) {
        const tiledImage = this.viewer.world.getItemAt(0);
        const OSD = window.OpenSeadragon;
        const pts = [[left, top], [left + width, top], [left, top + height], [left + width, top + height]]
            .map(([x, y]) => tiledImage.imageToViewerElementCoordinates(new OSD.Point(x, y)));
        const xs = pts.map(p => p.x);
        const ys = pts.map(p => p.y);
        return {
            topLeft: new OSD.Point(Math.min(...xs), Math.min(...ys)),
            bottomRight: new OSD.Point(Math.max(...xs), Math.max(...ys)),
        };
    }

    clearAiRegions() {
        this.canvas.getObjects().slice().forEach(o => {
            if (o.data && o.data.kind === 'ai-region') this.canvas.remove(o);
        });
        this.canvas.requestRenderAll();
    }

    cancelSelected() {
        this._cancelInProgressDraw();
        this.canvas.discardActiveObject();
        this.canvas.requestRenderAll();
    }

    // call on page change, before OSD opens the new image: whatever is on the
    // canvas belongs to the old one and would be redrawn at the new image's scale
    dropIfNot(imageId) {
        if (this._loadedImageId != null && String(this._loadedImageId) === String(imageId)) return;
        this._dropContents();
    }

    // keepWords: leave the OCR word layer (loadWordRegions) in place — it is
    // loaded alongside the annotations and may already be on the canvas
    _dropContents(keepWords = false) {
        this.canvas.getObjects().slice().forEach(o => {
            if (keepWords && o.data && o.data.kind === 'ocr-word') return;
            this.canvas.remove(o);
        });
        this._loadedUrl = null;
        this._loadedImageId = null;
        this.canvas.requestRenderAll();
    }

    async loadAnnotations(url) {
        if (url === this._loadedUrl || url === this._pendingUrl) return;

        const token = ++this._loadToken;
        this._pendingUrl = url;
        // runs after the fetch, so it must spare OCR words that loaded meanwhile
        const clear = () => this.canvas.getObjects().slice().forEach(o => {
            if (o.data && o.data.kind === 'ocr-word') return;
            this.canvas.remove(o);
        });
        // only the newest request owns _pendingUrl; an older one must not clear it
        const releasePending = () => { if (token === this._loadToken) this._pendingUrl = null; };

        this._dropContents();

        let payload;
        try {
            const res = await fetch(url, { credentials: 'same-origin' });
            payload = await res.json();
        } catch (e) {
            console.warn('[fabric-anno] load failed', e);
            releasePending();
            if (token === this._loadToken) this._dropContents(true);
            return;
        }
        // superseded by a newer load while we were fetching
        if (token !== this._loadToken) return;
        if (!Array.isArray(payload)) {
            releasePending();
            this._dropContents(true);
            return;
        }
        // legacy W3C annotations have no fabric `type` — filter to fabric-shaped entries
        const fabricEntries = payload.filter(p => p && typeof p === 'object' && typeof p.type === 'string');
        let objs;
        try {
            objs = await util.enlivenObjects(fabricEntries, {});
        } catch (e) {
            console.warn('[fabric-anno] enliven failed', e);
            // canvas untouched, so _loadedUrl still describes what's on it
            releasePending();
            return;
        }
        if (token !== this._loadToken) return;

        // the swap: canvas is created with renderOnAddRemove:false, so removing the
        // old objects and adding the new ones costs no intermediate repaint — the
        // single requestRenderAll() below is the only frame the viewer sees
        clear();
        this._suppressEvents = true;
        objs.forEach(obj => {
            if (!obj) return;
            // override saved colors so legacy green annotations display in current theme
            const t = obj.type;
            if (t === 'rect' || t === 'ellipse' || t === 'polygon') {
                obj.set({ stroke: COLOR_STROKE, fill: COLOR_FILL_SHAPE });
            } else if (t === 'line') {
                obj.set({ stroke: COLOR_STROKE });
            }
            // apply consistent, noticeable selection visuals
            obj.set(SELECTION_STYLE);
            // disable object caching to match fresh-draw behavior. on huge images
            // (image-px coords) the cache canvas hits fabric's maxCacheSideLimit and
            // renders blank, so cached reloaded annotations vanish; fresh shapes
            // already set objectCaching:false and stay visible.
            obj.objectCaching = false;
            // textbox: leave fill/backgroundColor untouched (white-on-black is intentional)
            this.canvas.add(obj);
        });
        this._suppressEvents = false;
        this._loadedUrl = url;
        this._loadedImageId = this.imageId;
        releasePending();
        this._hideUntilPositioned();
        this._setReadOnlyDom(this._readOnly);
        this._updateViewportTransform();
        this.canvas.requestRenderAll();
    }

    // Renders OCR word/region highlights (image_ai_regions rows) as a separate,
    // non-persisted layer — tagged via `.data.kind` so it's never picked up by
    // the save/update/delete flows above and never touched by loadAnnotations().
    loadWordRegions(regions, imgWidth, imgHeight) {
        this.canvas.getObjects().slice().forEach(o => {
            if (o.data && o.data.kind === 'ocr-word') this.canvas.remove(o);
        });
        this._hidePopover();
        if (!Array.isArray(regions) || !imgWidth || !imgHeight) {
            this.canvas.requestRenderAll();
            return;
        }
        // placeholder width; _refreshOcrStrokeWidths() (called below) sets the
        // real value and keeps it correct on every later viewport update too
        const strokeW = WORD_STROKE_BASE_PX / (this._homeImageScale() || 1);
        regions.forEach(r => {
            const isWord = r.granularity === 'word';
            const width = (r.bbox_width || 0) * imgWidth;
            const height = (r.bbox_height || 0) * imgHeight;
            if (width <= 0 || height <= 0) return;
            const color = ROLE_COLORS[r.role] || DEFAULT_ROLE_COLOR;
            // mapKurator words carry their real outline (curved/rotated labels);
            // everything else is drawn as its bbox. A Polygon's left/top/width/
            // height are its envelope, so zoom-to-word and hit-testing still work.
            const points = Array.isArray(r.polygon) && r.polygon.length >= 3
                ? r.polygon.map(([x, y]) => ({ x: x * imgWidth, y: y * imgHeight }))
                : null;
            const shape = points ? Polygon : Rect;
            const rect = new shape(...(points ? [points] : []), {
                ...(points ? {} : {
                    left: (r.bbox_left || 0) * imgWidth,
                    top: (r.bbox_top || 0) * imgHeight,
                    width,
                    height,
                }),
                fill: isWord ? hexToRgba(color, 0.16) : 'transparent',
                stroke: color,
                strokeWidth: strokeW,
                strokeUniform: true,
                selectable: false,
                evented: false,
                hasControls: false,
                hasBorders: false,
                hoverCursor: 'pointer',
                objectCaching: false,
                excludeFromExport: true,
                visible: !isWord,
            });
            rect.data = { kind: 'ocr-word', granularity: r.granularity, role: r.role, text: r.text || '', points };
            this.canvas.add(rect);
        });
        this._refreshOcrStrokeWidths();
        this._updateWordVisibility();
        this.canvas.requestRenderAll();
    }

    // Deep-link entry point: zooms/pans to the first word-level region whose
    // OCR text matches `term` (used when arriving from a page-text search
    // result) and opens its popover once the viewport settles. Prefers an
    // exact (case-insensitive) match over a substring match, since a search
    // term is usually a single word but may also be a fragment of one.
    // Returns false if no matching word region is currently loaded.
    highlightSearchTerm(term) {
        const needle = (term || '').trim().toLowerCase();
        if (!needle) return false;

        const words = this.canvas.getObjects().filter(o => o.data && o.data.kind === 'ocr-word' && o.data.granularity === 'word');
        const match = words.find(o => (o.data.text || '').toLowerCase() === needle)
            || words.find(o => (o.data.text || '').toLowerCase().includes(needle));
        if (!match) return false;

        const OSD = window.OpenSeadragon;
        const padX = match.width * 4;
        const padY = match.height * 4;
        const imgRect = new OSD.Rect(match.left - padX, match.top - padY, match.width + padX * 2, match.height + padY * 2);
        const viewportRect = this.viewer.viewport.imageToViewportRectangle(imgRect);
        this.viewer.viewport.fitBounds(viewportRect, false);

        this.viewer.addOnceHandler('animation-finish', () => {
            const center = this.viewer.viewport.imageToViewportCoordinates(
                new OSD.Point(match.left + match.width / 2, match.top + match.height / 2)
            );
            const pixel = this.viewer.viewport.viewportToViewerElementCoordinates(center);
            match.visible = true;
            this._showRegionPopover(match, pixel);
        });
        return true;
    }

    // Keeps AI-detected region/word borders at a consistent on-screen thickness.
    // Re-run on every viewport update (see _updateViewportTransform) instead of
    // computing once at load time: a one-shot value baked in from whatever the
    // live zoom happened to be at that instant (e.g. mid page-transition, before
    // OSD settles on the new image) would otherwise stick permanently, which is
    // why borders used to look thick on some images and thin on most.
    _refreshOcrStrokeWidths() {
        const cur = this._currentImageScale();
        if (!cur) return;
        const strokeW = WORD_STROKE_BASE_PX / cur;
        this.canvas.getObjects().forEach(o => {
            if (o.data && o.data.kind === 'ocr-word') o.set('strokeWidth', strokeW);
        });
    }

    _updateWordVisibility() {
        const cur = this._currentImageScale();
        const home = this._homeImageScale();
        if (!cur || !home) return;
        const show = (cur / home) >= WORD_ZOOM_THRESHOLD;
        let changed = false;
        this.canvas.getObjects().forEach(o => {
            if (o.data && o.data.kind === 'ocr-word' && o.data.granularity === 'word' && o.visible !== show) {
                o.visible = show;
                changed = true;
            }
        });
        if (changed) this.canvas.requestRenderAll();
    }

    _findWordRegionAt(x, y) {
        let best = null;
        let bestArea = Infinity;
        this.canvas.getObjects().forEach(o => {
            if (!o.data || o.data.kind !== 'ocr-word' || o.visible === false) return;
            if (x < o.left || x > o.left + o.width || y < o.top || y > o.top + o.height) return;
            if (o.data.points && !pointInPolygon(x, y, o.data.points)) return;
            const area = o.width * o.height;
            if (area < bestArea) {
                bestArea = area;
                best = o;
            }
        });
        return best;
    }

    _handleCanvasClick(opt) {
        if (!opt.quick) return; // ignore drag-releases
        const viewport = this.viewer.viewport.pointFromPixel(opt.position);
        const imgPoint = this.viewer.viewport.viewportToImageCoordinates(viewport);
        const hit = this._findWordRegionAt(imgPoint.x, imgPoint.y);
        if (hit) {
            // stop OSD's default click-to-zoom so clicking a highlight just opens
            // its popover instead of zooming in
            opt.preventDefaultAction = true;
            this._showRegionPopover(hit, opt.position);
        } else {
            this._hidePopover();
        }
    }

    _showRegionPopover(rectObj, pos) {
        this._hidePopover();
        const { role, text } = rectObj.data;
        const label = ROLE_LABELS[role] || role;
        const pop = document.createElement('div');
        pop.className = 'ocr-region-popover';
        pop.innerHTML = `<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">` +
                `<span style="font-weight:600;">${escapeHtml(label)}</span>` +
                (text ? `<button type="button" class="ocr-region-popover-copy" title="Copy text">${COPY_ICON}</button>` : '') +
            `</div>` +
            (text ? `<div style="margin-top:2px;opacity:0.85;">${escapeHtml(text)}</div>` : '');
        const copyBtn = pop.querySelector('.ocr-region-popover-copy');
        if (copyBtn) {
            Object.assign(copyBtn.style, {
                pointerEvents: 'auto', // popover itself stays click-through
                background: 'transparent',
                border: 'none',
                color: '#fff',
                opacity: '0.8',
                padding: '2px',
                lineHeight: '0',
                cursor: 'pointer',
                flexShrink: '0',
            });
            // keep the press away from OSD's MouseTracker, which would treat it
            // as a canvas click (zoom / hide this popover)
            ['pointerdown', 'mousedown', 'touchstart', 'pointerup', 'mouseup', 'click'].forEach(evt =>
                copyBtn.addEventListener(evt, e => e.stopPropagation()));
            copyBtn.addEventListener('click', () => {
                copyToClipboard(text).then(() => {
                    copyBtn.innerHTML = CHECK_ICON;
                    setTimeout(() => { copyBtn.innerHTML = COPY_ICON; }, 1200);
                });
            });
        }
        Object.assign(pop.style, {
            position: 'absolute',
            left: `${pos.x}px`,
            top: `${pos.y - 10}px`,
            transform: 'translate(-50%, -100%)',
            background: 'rgba(20,20,20,0.95)',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '13px',
            lineHeight: '1.4',
            maxWidth: '260px',
            zIndex: 50,
            pointerEvents: 'none',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
        });
        this.overlayWrap.appendChild(pop);
        this._activePopover = pop;
    }

    _hidePopover() {
        if (this._activePopover) {
            this._activePopover.remove();
            this._activePopover = null;
        }
    }

    destroy() {
        this.viewer.removeHandler('update-viewport', this._onViewportChange);
        this.viewer.removeHandler('resize', this._onViewportChange);
        this.viewer.removeHandler('open', this._onViewportChange);
        this.viewer.removeHandler('canvas-click', this._onCanvasClick);
        this.viewer.removeHandler('canvas-drag', this._onNavigate);
        this.viewer.removeHandler('canvas-pinch', this._onNavigate);
        this.viewer.removeHandler('canvas-scroll', this._onNavigate);
        if (this._onKeyDown) document.removeEventListener('keydown', this._onKeyDown);
        if (this._onKeyUp) document.removeEventListener('keyup', this._onKeyUp);
        if (this._onBlur) window.removeEventListener('blur', this._onBlur);
        this._hidePopover();
        try { this.canvas.dispose(); } catch (_) {}
        if (this.overlayWrap && this.overlayWrap.parentNode) {
            this.overlayWrap.parentNode.removeChild(this.overlayWrap);
        }
        this._handlers = { createAnnotation: [], updateAnnotation: [], deleteAnnotation: [] };
    }

    get readOnly() { return this._readOnly; }
    set readOnly(val) {
        this._readOnly = !!val;
        this._setReadOnlyDom(this._readOnly);
        if (this._readOnly) this._cancelInProgressDraw();
    }

    // ── DOM/canvas setup ────────────────────────────────────────────────────

    _initOverlay() {
        // mount inside .openseadragon-container (viewer.container) — not viewer.element —
        // so the overlay tracks the canvas area when .shrink is toggled by .dots-wrapper / .content-wrapper.
        const container = this.viewer.container || this.viewer.element;
        const wrap = document.createElement('div');
        wrap.className = 'fabric-osd-overlay';
        Object.assign(wrap.style, {
            position: 'absolute',
            inset: '0',
            pointerEvents: 'none',
        });
        const canvasEl = document.createElement('canvas');
        wrap.appendChild(canvasEl);
        container.appendChild(wrap);

        this.overlayWrap = wrap;
        this.canvas = new Canvas(canvasEl, {
            selection: false,
            backgroundColor: 'transparent',
            preserveObjectStacking: true,
            renderOnAddRemove: false,
        });
        // alt-held during corner-handle drag → uniform scale (locks aspect ratio).
        // shift is reserved for draw-enable, so override fabric's default 'shiftKey'.
        this.canvas.uniScaleKey = 'altKey';

        // fabric replaces canvasEl with a wrapper containing two canvases — both stay inside our wrap.
        if (this.canvas.wrapperEl && this.canvas.wrapperEl.style) {
            this.canvas.wrapperEl.style.position = 'absolute';
            this.canvas.wrapperEl.style.inset = '0';
        }
    }

    _setReadOnlyDom(readOnly) {
        if (!this.canvas || !this.canvas.wrapperEl) return;
        // keep the rubber-band group selection off so its translucent blue box
        // never renders under an in-progress shape drag (individual shapes are
        // still selectable via per-object `selectable`/`evented` below)
        this.canvas.selection = false;
        this.canvas.getObjects().forEach(o => {
            // word/region highlights are a display-only layer clicked via OSD's
            // canvas-click handler, never fabric's own selection system
            if (o.data && o.data.kind === 'ocr-word') return;
            // AI selection rectangles are display-only; while selecting, persisted
            // shapes must not swallow the drag either
            const inert = (o.data && o.data.kind === 'ai-region') || this._selectionOnly;
            o.selectable = !readOnly && !inert;
            o.evented = !readOnly && !inert;
            if (o.isType && o.isType('textbox')) {
                o.editable = !readOnly;
            }
        });
        this._updatePointerEvents();
        this.canvas.requestRenderAll();
    }

    // pointer-events on the fabric canvas wrapper drive who sees the click:
    //   - readOnly true                 → none (OSD handles everything; nothing draws)
    //   - shift held                    → auto (start a draw / select existing shape)
    //   - drawing in progress / textbox editing → auto (don't lose mid-gesture)
    //   - else                          → none (default OSD pan/zoom)
    _updatePointerEvents() {
        if (!this.canvas || !this.canvas.wrapperEl) return;
        if (this._readOnly) {
            this.canvas.wrapperEl.style.pointerEvents = 'none';
            return;
        }
        const drawing = !!this._activeDrawing || !!this._polyState;
        const active = this.canvas.getActiveObject();
        const editingText = !!(active && active.isEditing);
        const shouldCapture = this._selectionOnly || this._shiftHeld || drawing || editingText;
        this.canvas.wrapperEl.style.pointerEvents = shouldCapture ? 'auto' : 'none';
    }

    _bindShiftTracking() {
        this._onKeyDown = e => {
            if (e.key === 'Shift' && !this._shiftHeld) {
                this._shiftHeld = true;
                this._updatePointerEvents();
            }
        };
        this._onKeyUp = e => {
            if (e.key === 'Shift' && this._shiftHeld) {
                this._shiftHeld = false;
                this._updatePointerEvents();
            }
        };
        // window blur: in case shift is released outside the page
        this._onBlur = () => {
            if (this._shiftHeld) {
                this._shiftHeld = false;
                this._updatePointerEvents();
            }
        };
        document.addEventListener('keydown', this._onKeyDown);
        document.addEventListener('keyup', this._onKeyUp);
        window.addEventListener('blur', this._onBlur);
    }

    // ── OSD coordinate sync ─────────────────────────────────────────────────

    _bindOSD() {
        this._onViewportChange = () => this._updateOverlaySize();
        this.viewer.addHandler('update-viewport', this._onViewportChange);
        this.viewer.addHandler('resize', this._onViewportChange);
        this.viewer.addHandler('open', this._onViewportChange);

        // OSD-level click (not fabric's own mouse:down) so word/region highlights
        // stay clickable without touching the readOnly pointer-events toggle that
        // gates drawing/selection.
        this._onCanvasClick = opt => this._handleCanvasClick(opt);
        this.viewer.addHandler('canvas-click', this._onCanvasClick);

        // dismiss the popover only on real user-initiated navigation, not on
        // 'update-viewport' (fires on nearly every redraw tick, including ones
        // caused by the click that opened the popover — hiding it there made
        // the popover flash shut immediately after appearing)
        this._onNavigate = () => this._hidePopover();
        this.viewer.addHandler('canvas-drag', this._onNavigate);
        this.viewer.addHandler('canvas-pinch', this._onNavigate);
        this.viewer.addHandler('canvas-scroll', this._onNavigate);
    }

    _updateOverlaySize() {
        const size = this.viewer.viewport.getContainerSize();
        if (this.canvas.getWidth() !== size.x || this.canvas.getHeight() !== size.y) {
            this.canvas.setDimensions({ width: size.x, height: size.y });
        }
        this._updateViewportTransform();
    }

    // false when OSD has no tiled image yet (between goToPage and open)
    _updateViewportTransform() {
        const tiledImage = this.viewer.world.getItemAt(0);
        if (!tiledImage) return false;
        const OSD = window.OpenSeadragon;
        if (!OSD) return false;
        // full affine from the image axes, so OSD rotation carries over to fabric
        const o = tiledImage.imageToViewerElementCoordinates(new OSD.Point(0, 0));
        const ux = tiledImage.imageToViewerElementCoordinates(new OSD.Point(1, 0));
        const uy = tiledImage.imageToViewerElementCoordinates(new OSD.Point(0, 1));
        const scale = Math.hypot(ux.x - o.x, ux.y - o.y);
        if (!isFinite(scale) || scale <= 0) return false;
        this.canvas.setViewportTransform([ux.x - o.x, ux.y - o.y, uy.x - o.x, uy.y - o.y, o.x, o.y]);
        this._applyCounterScales(scale);
        this._refreshOcrStrokeWidths();
        this._updateWordVisibility();
        this._reveal();
        this.canvas.requestRenderAll();
        return true;
    }

    // objects are in image-pixel coords: untransformed they draw wrong
    _hideUntilPositioned() {
        this._awaitingPosition = true;
        if (this.overlayWrap) this.overlayWrap.style.visibility = 'hidden';
    }

    _reveal() {
        if (!this._awaitingPosition) return;
        this._awaitingPosition = false;
        if (this.overlayWrap) this.overlayWrap.style.visibility = '';
    }

    _applyCounterScales(scale) {
        const base = this._homeImageScale();
        if (!base) return;
        const factor = Math.pow(scale / base, COUNTER_SCALE_EXPONENT - 1);
        this.canvas.getObjects().forEach(o => {
            if (!o.counterScale) return;
            const intX = (o.intendedScaleX != null) ? o.intendedScaleX : 1;
            const intY = (o.intendedScaleY != null) ? o.intendedScaleY : 1;
            o.scaleX = intX * factor;
            o.scaleY = intY * factor;
            o.setCoords();
        });
    }

    // ── drawing ─────────────────────────────────────────────────────────────

    _bindFabricEvents() {
        this.canvas.on('mouse:down', opt => this._onMouseDown(opt));
        this.canvas.on('mouse:move', opt => this._onMouseMove(opt));
        this.canvas.on('mouse:up', opt => this._onMouseUp(opt));
        this.canvas.on('mouse:dblclick', opt => this._onDblClick(opt));
        this.canvas.on('object:modified', opt => this._onObjectModified(opt));
        // any textbox entering / exiting editing flips pointer-events
        this.canvas.on('text:editing:entered', () => this._updatePointerEvents());
        this.canvas.on('text:editing:exited', opt => this._onTextEditingExited(opt));
        this.canvas.on('selection:cleared', () => this._updatePointerEvents());

        // forward wheel to OSD so user can zoom in edit mode
        const upper = this.canvas.upperCanvasEl;
        upper.addEventListener('wheel', e => {
            if (this._readOnly) return;
            e.preventDefault();
            const OSD = window.OpenSeadragon;
            const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
            const px = new OSD.Point(e.offsetX, e.offsetY);
            const ref = this.viewer.viewport.pointFromPixel(px);
            this.viewer.viewport.zoomBy(factor, ref);
            this.viewer.viewport.applyConstraints();
        }, { passive: false });

        // delete key removes one or many selected shapes; escape cancels selection / in-progress draw
        document.addEventListener('keydown', e => {
            if (this._readOnly) return;
            if (e.key === 'Escape') {
                const active = this.canvas.getActiveObject();
                if (active && active.isEditing && active.isType && active.isType('textbox')) {
                    if (active._pendingCreate) {
                        // discard the pending textbox without persisting
                        this._cancelInProgressDraw();
                    } else {
                        // committed text: just exit editing, content preserved
                        active.exitEditing();
                    }
                    e.preventDefault();
                    return;
                }
                this.cancelSelected();
                return;
            }
            if (e.key !== 'Delete' && e.key !== 'Backspace') return;
            const targets = this.canvas.getActiveObjects();
            if (!targets || !targets.length) return;
            // backspace inside an editing textbox = normal text deletion
            if (targets.some(o => o.isType && o.isType('textbox') && o.isEditing)) return;
            this.canvas.discardActiveObject();
            const ids = [];
            targets.forEach(o => {
                if (o.id) ids.push(o.id);
                this.canvas.remove(o);
            });
            this.canvas.requestRenderAll();
            ids.forEach(id => {
                this._fire('deleteAnnotation', { id });
                this._postJson('/delete-annotorious', { id });
            });
        });
    }

    _onMouseDown(opt) {
        if (this._readOnly) return;
        const pointer = this.canvas.getPointer(opt.e);

        // polygon-in-progress always routes here, even over existing shapes
        if (this._polyState) {
            this._polygonClick(pointer);
            this._stopBubble(opt.e);
            return;
        }

        if (opt.target) {
            // selecting an existing shape: stop the click from bubbling to OSD
            // so OSD doesn't steal focus from a textbox that's about to enter editing
            this._stopBubble(opt.e);
            return;
        }

        let handled = false;
        switch (this.tool) {
            case 'ai-region': this._startRect(pointer, true); handled = true; break;
            case 'rect': this._startRect(pointer); handled = true; break;
            case 'circle': this._startCircle(pointer); handled = true; break;
            case 'line': this._startLine(pointer); handled = true; break;
            case 'polygon': this._polygonClick(pointer); handled = true; break;
            case 'text': this._addText(pointer); handled = true; break;
        }
        if (handled) this._stopBubble(opt.e);
    }

    _stopBubble(e) {
        if (!e) return;
        if (e.stopPropagation) e.stopPropagation();
        if (e.preventDefault) e.preventDefault();
    }

    _onMouseMove(opt) {
        if (this._readOnly) return;
        const pointer = this.canvas.getPointer(opt.e);

        if (this._activeDrawing) {
            const a = this._activeDrawing;
            switch (a.kind) {
                case 'rect': {
                    let w = pointer.x - a.start.x;
                    let h = pointer.y - a.start.y;
                    // hold alt to lock aspect ratio (square) — shift is taken by draw-enable
                    if (opt.e && opt.e.altKey) {
                        const s = Math.max(Math.abs(w), Math.abs(h));
                        w = w < 0 ? -s : s;
                        h = h < 0 ? -s : s;
                    }
                    a.obj.set({
                        left: Math.min(a.start.x, a.start.x + w),
                        top: Math.min(a.start.y, a.start.y + h),
                        width: Math.abs(w),
                        height: Math.abs(h),
                    });
                    break;
                }
                case 'circle': {
                    let dx = pointer.x - a.start.x;
                    let dy = pointer.y - a.start.y;
                    // hold alt to lock aspect ratio (perfect circle)
                    if (opt.e && opt.e.altKey) {
                        const s = Math.max(Math.abs(dx), Math.abs(dy));
                        dx = dx < 0 ? -s : s;
                        dy = dy < 0 ? -s : s;
                    }
                    a.obj.set({
                        left: Math.min(a.start.x, a.start.x + dx),
                        top: Math.min(a.start.y, a.start.y + dy),
                        rx: Math.abs(dx) / 2,
                        ry: Math.abs(dy) / 2,
                    });
                    break;
                }
                case 'line':
                    a.obj.set({ x2: pointer.x, y2: pointer.y });
                    break;
            }
            a.obj.setCoords();
            this.canvas.requestRenderAll();
        } else if (this._polyState) {
            // animate the preview segment
            const pts = this._polyState.points;
            if (pts.length === 0) return;
            const last = pts[pts.length - 1];
            this._polyState.previewLine.set({ x1: last.x, y1: last.y, x2: pointer.x, y2: pointer.y });
            this._polyState.previewLine.setCoords();
            this.canvas.requestRenderAll();
        }
    }

    _onDblClick(opt) {
        if (this._readOnly) return;
        if (this._polyState && this._polyState.points.length >= 3) {
            this._finishPolygon();
        }
    }

    _onMouseUp(opt) {
        if (!this._activeDrawing) return;
        const a = this._activeDrawing;
        // cleared first: aiRegionSelected handlers cancel any in-progress draw
        this._activeDrawing = null;
        // convert "minimum size" from screen px to current image px
        const scale = this._currentImageScale() || 1;
        const minImg = MIN_SHAPE_SCREEN_PX / scale;
        let tooSmall = false;
        if (a.kind === 'rect') {
            tooSmall = a.obj.width < minImg || a.obj.height < minImg;
        } else if (a.kind === 'circle') {
            tooSmall = a.obj.rx * 2 < minImg || a.obj.ry * 2 < minImg;
        } else if (a.kind === 'line') {
            tooSmall = Math.hypot(a.obj.x2 - a.obj.x1, a.obj.y2 - a.obj.y1) < minImg;
        }
        if (tooSmall) {
            this.canvas.remove(a.obj);
        } else if (a.ai) {
            this._finishAiRegion(a.obj);
        } else {
            a.obj.set({ id: uuid(), shapeType: a.kind });
            this._persistCreate(a.obj);
        }
        this._updatePointerEvents();
        this.canvas.requestRenderAll();
    }

    _startRect(pointer, ai = false) {
        const r = new Rect({
            left: pointer.x,
            top: pointer.y,
            width: 0,
            height: 0,
            fill: ai ? hexToRgba(COLOR_AI_REGION, 0.12) : COLOR_FILL_SHAPE,
            stroke: ai ? COLOR_AI_REGION : COLOR_STROKE,
            strokeWidth: STROKE_BASE_PX / (this._homeImageScale() || 1),
            strokeDashArray: ai ? [8, 5] : null,
            strokeUniform: true,
            objectCaching: false,
            ...SELECTION_STYLE,
        });
        this.canvas.add(r);
        this._activeDrawing = { kind: 'rect', ai, obj: r, start: pointer };
    }

    // one selection at a time: drop the previous rectangle, keep this one on the
    // canvas (inert) and hand its image-fraction bbox + a screen anchor to the page
    _finishAiRegion(obj) {
        this.clearAiRegions();
        obj.set({ selectable: false, evented: false, hasControls: false, hasBorders: false });
        obj.data = { kind: 'ai-region' };

        const tiledImage = this.viewer.world.getItemAt(0);
        const size = tiledImage ? tiledImage.getContentSize() : null;
        if (!size || !size.x || !size.y) return;

        // clip to the image so the outline, the anchor and the region sent all agree
        const x0 = Math.max(0, obj.left), y0 = Math.max(0, obj.top);
        const x1 = Math.min(size.x, obj.left + obj.width), y1 = Math.min(size.y, obj.top + obj.height);
        if (x1 <= x0 || y1 <= y0) { this.canvas.remove(obj); return; }
        obj.set({ left: x0, top: y0, width: x1 - x0, height: y1 - y0 });
        obj.setCoords();

        const region = {
            left: x0 / size.x,
            top: y0 / size.y,
            width: (x1 - x0) / size.x,
            height: (y1 - y0) / size.y,
        };
        const anchor = this._screenBBox(obj.left, obj.top, obj.width, obj.height);
        this._fire('aiRegionSelected', { region, anchor });
    }

    _startCircle(pointer) {
        const e = new Ellipse({
            left: pointer.x,
            top: pointer.y,
            rx: 0,
            ry: 0,
            fill: COLOR_FILL_SHAPE,
            stroke: COLOR_STROKE,
            strokeWidth: STROKE_BASE_PX / (this._homeImageScale() || 1),
            strokeUniform: true,
            objectCaching: false,
            ...SELECTION_STYLE,
        });
        this.canvas.add(e);
        this._activeDrawing = { kind: 'circle', obj: e, start: pointer };
    }

    _startLine(pointer) {
        const l = new Line([pointer.x, pointer.y, pointer.x, pointer.y], {
            stroke: COLOR_STROKE,
            strokeWidth: STROKE_BASE_PX / (this._homeImageScale() || 1),
            strokeUniform: true,
            objectCaching: false,
            ...SELECTION_STYLE,
        });
        this.canvas.add(l);
        this._activeDrawing = { kind: 'line', obj: l, start: pointer };
    }

    _addText(pointer) {
        const factor = this._counterScaleFactor();
        // fontSize is stored in image-pixel coords; convert TEXT_BASE_FONT_PX (a
        // screen-pixel intent) into image-px via the current baseScale so the
        // displayed size at base zoom is identical regardless of how big the
        // underlying image is. Without this, large staging images render text
        // much smaller than smaller dev images.
        const baseScale = this._homeImageScale() || 1;
        const fontSizeImg = TEXT_BASE_FONT_PX / baseScale;
        const tb = new Textbox('', {
            left: pointer.x,
            top: pointer.y,
            width: fontSizeImg * 8,
            fontSize: fontSizeImg,
            fontFamily: 'sans-serif',
            fill: COLOR_FILL_TEXT,
            backgroundColor: COLOR_TEXT_BG,
            padding: 2,
            scaleX: factor,
            scaleY: factor,
            editable: true,
            ...SELECTION_STYLE,
        });
        tb.set({ id: uuid(), shapeType: 'text', counterScale: true, intendedScaleX: 1, intendedScaleY: 1 });
        tb._pendingCreate = true;
        this.canvas.add(tb);
        this.canvas.setActiveObject(tb);
        tb.enterEditing();
        this._updatePointerEvents();
        tb.on('editing:exited', () => {
            if (tb._discarding) return;
            // only persist a non-empty pending create here. The empty case
            // (and removal) is handled by the canvas-level _onTextEditingExited,
            // which inspects _pendingCreate to decide whether to propagate a
            // delete — so leave the flag intact when text is empty.
            if (tb._pendingCreate) {
                const text = (tb.text || '').trim();
                if (text) {
                    tb._pendingCreate = false;
                    tb._skipNextModified = true;
                    this._persistCreate(tb);
                }
            }
        });
    }

    // canvas-level text editing exit: removes any textbox that was emptied. If
    // it was a previously-committed annotation, also propagate the delete.
    _onTextEditingExited(opt) {
        const tb = opt && opt.target;
        if (tb && !tb._discarding && tb.isType && tb.isType('textbox')) {
            const text = (tb.text || '').trim();
            if (!text) {
                const wasPendingCreate = !!tb._pendingCreate;
                const id = tb.id;
                tb._pendingCreate = false;
                this.canvas.remove(tb);
                // pending creates were never saved server-side, so don't fire delete
                if (!wasPendingCreate && id) {
                    this._fire('deleteAnnotation', { id });
                    this._postJson('/delete-annotorious', { id });
                }
                this.canvas.requestRenderAll();
            }
        }
        this._updatePointerEvents();
    }

    // polygon: click to add points; click near first point or double-click to close
    _polygonClick(pointer) {
        if (!this._polyState) {
            const previewLine = this._mkPolyHelperLine(pointer, pointer, true);
            this.canvas.add(previewLine);
            this._polyState = { points: [pointer], previewLine, segments: [], dots: [] };
            this._addPolygonDot(pointer);
            return;
        }
        const pts = this._polyState.points;
        const first = pts[0];
        const curScale = this._currentImageScale() || 1;
        const distImg = Math.hypot(pointer.x - first.x, pointer.y - first.y);
        if (pts.length >= 3 && distImg < (POLYGON_CLOSE_THRESHOLD_PX / curScale)) {
            this._finishPolygon();
            return;
        }
        // freeze a static segment from previous point to new point
        const last = pts[pts.length - 1];
        const seg = this._mkPolyHelperLine(last, pointer, false);
        this.canvas.add(seg);
        this._polyState.segments.push(seg);
        pts.push(pointer);
        this._addPolygonDot(pointer);
    }

    _mkPolyHelperLine(a, b, dashed) {
        return new Line([a.x, a.y, b.x, b.y], {
            stroke: COLOR_STROKE,
            strokeDashArray: dashed ? [5, 5] : null,
            strokeWidth: STROKE_BASE_PX / (this._homeImageScale() || 1),
            strokeUniform: true,
            selectable: false,
            evented: false,
            excludeFromExport: true,
        });
    }

    _addPolygonDot(pointer) {
        const dot = new Circle({
            left: pointer.x,
            top: pointer.y,
            radius: 4 / (this._currentImageScale() || 1),
            originX: 'center',
            originY: 'center',
            fill: COLOR_STROKE,
            selectable: false,
            evented: false,
            excludeFromExport: true,
        });
        this.canvas.add(dot);
        this._polyState.dots.push(dot);
    }

    _finishPolygon() {
        const pts = this._polyState.points.map(p => ({ x: p.x, y: p.y }));
        this.canvas.remove(this._polyState.previewLine);
        this._polyState.segments.forEach(s => this.canvas.remove(s));
        this._polyState.dots.forEach(d => this.canvas.remove(d));
        this._polyState = null;
        this._updatePointerEvents();

        // need ≥3 points AND a bounding box bigger than the min-size threshold
        const scale = this._currentImageScale() || 1;
        const minImg = MIN_SHAPE_SCREEN_PX / scale;
        const xs = pts.map(p => p.x);
        const ys = pts.map(p => p.y);
        const bboxW = Math.max(...xs) - Math.min(...xs);
        const bboxH = Math.max(...ys) - Math.min(...ys);
        if (pts.length < 3 || bboxW < minImg || bboxH < minImg) {
            this.canvas.requestRenderAll();
            return;
        }
        const poly = new Polygon(pts, {
            fill: COLOR_FILL_SHAPE,
            stroke: COLOR_STROKE,
            strokeWidth: STROKE_BASE_PX / (this._homeImageScale() || 1),
            strokeUniform: true,
            objectCaching: false,
            ...SELECTION_STYLE,
        });
        poly.set({ id: uuid(), shapeType: 'polygon' });
        this.canvas.add(poly);
        this._persistCreate(poly);
        this.canvas.requestRenderAll();
    }

    _cancelInProgressDraw() {
        if (this._activeDrawing) {
            this.canvas.remove(this._activeDrawing.obj);
            this._activeDrawing = null;
        }
        if (this._polyState) {
            this.canvas.remove(this._polyState.previewLine);
            this._polyState.segments.forEach(s => this.canvas.remove(s));
            this._polyState.dots.forEach(d => this.canvas.remove(d));
            this._polyState = null;
        }
        // discard a textbox still in its initial create flow. Mark _discarding
        // so the editing:exited handlers know to bail out (no persist, no delete)
        const active = this.canvas.getActiveObject();
        if (active && active._pendingCreate && active.isType && active.isType('textbox')) {
            active._discarding = true;
            if (active.isEditing) active.exitEditing();
            this.canvas.remove(active);
        }
        this._updatePointerEvents();
        this.canvas.requestRenderAll();
    }

    _counterScaleFactor() {
        const base = this._homeImageScale();
        const cur = this._currentImageScale();
        if (!base || !cur) return 1;
        return Math.pow(cur / base, COUNTER_SCALE_EXPONENT - 1);
    }

    _currentImageScale() {
        const tiledImage = this.viewer.world.getItemAt(0);
        if (!tiledImage) return null;
        const OSD = window.OpenSeadragon;
        const origin = tiledImage.imageToViewerElementCoordinates(new OSD.Point(0, 0));
        const xUnit = tiledImage.imageToViewerElementCoordinates(new OSD.Point(1, 0));
        return Math.hypot(xUnit.x - origin.x, xUnit.y - origin.y);
    }

    // home-zoom image→screen scale for the CURRENT image, derived live so it stays
    // correct after switching images (no stale cached base). scale ∝ zoom, so
    // homeScale = currentScale * homeZoom / currentZoom.
    _homeImageScale() {
        const cur = this._currentImageScale();
        if (!cur) return null;
        const vp = this.viewer.viewport;
        const z = vp.getZoom(true);
        const hz = vp.getHomeZoom();
        if (!z || !hz) return cur;
        return cur * (hz / z);
    }

    // ── persistence ─────────────────────────────────────────────────────────

    _onObjectModified(opt) {
        const obj = opt && opt.target;
        if (!obj || !obj.id || this._suppressEvents) return;
        if (obj._pendingCreate) return;
        if (obj._skipNextModified) {
            obj._skipNextModified = false;
            return;
        }
        // recapture user's intended scale (at base zoom) after resize/move/rotate
        if (obj.counterScale) {
            const factor = this._counterScaleFactor() || 1;
            obj.intendedScaleX = obj.scaleX / factor;
            obj.intendedScaleY = obj.scaleY / factor;
        }
        const json = obj.toObject(PERSIST_KEYS);
        json.id = obj.id;
        this._fire('updateAnnotation', json);
        this._postJson('/update-annotorious', json);
    }

    /** @returns {Promise<boolean>|undefined} resolves to whether the server accepted the shape */
    _persistCreate(obj) {
        if (this._suppressEvents) return;
        const json = obj.toObject(PERSIST_KEYS);
        json.id = obj.id;
        this._fire('createAnnotation', json);
        return this._postJson('/save-annotorious', { anno: json, imageId: this.imageId });
    }

    // Promote the current AI selection rectangle to a persisted shape annotation —
    // same look, JSON and endpoint as a rect drawn with the admin tool, so it reloads
    // through loadAnnotations() like any other. Resolves false when the save failed,
    // null when there is no selection.
    persistAiRegion() {
        const obj = this.canvas.getObjects().find(o => o.data && o.data.kind === 'ai-region');
        if (!obj) return Promise.resolve(null);
        obj.set({
            id: uuid(),
            shapeType: 'rect',
            stroke: COLOR_STROKE,
            fill: COLOR_FILL_SHAPE,
            strokeDashArray: null,
            hasControls: true,
            hasBorders: true,
        });
        delete obj.data;
        this._setReadOnlyDom(this.readOnly);   // selectable/evented like any loaded shape
        this.canvas.requestRenderAll();
        return this._persistCreate(obj) || Promise.resolve(false);
    }

    /** @returns {Promise<boolean>} true when the server answered 2xx */
    _postJson(url, body) {
        return fetch(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': this.csrfToken,
                'X-Requested-With': 'XMLHttpRequest',
            },
            body: JSON.stringify(body),
        }).then(res => {
            if (!res.ok) console.warn('[fabric-anno] save rejected', res.status);
            return res.ok;
        }).catch(err => { console.warn('[fabric-anno] save failed', err); return false; });
    }

    _fire(event, data) {
        (this._handlers[event] || []).forEach(fn => {
            try { fn(data); } catch (e) { console.error(e); }
        });
    }
}

// Convenience factory matching the old `OpenSeadragon.Annotorious(viewer, opts)` shape.
export function createFabricAnnotator(viewer, opts) {
    return new FabricAnnotator(viewer, opts);
}
